import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemButton, ListItemText, TextField } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Recipe } from "../../models/Recipe";
import { MealPlanEntry } from "../../models/MealPlanEntry";
import { getAllRecipes } from "../../utils/RecipeService";
import { saveMealPlanEntry } from "../../utils/MealPlanService";

export type RecipePickerDialogProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    date: string;
    mealType: 'BREAKFAST' | 'LUNCH' | 'DINNER';
    existingEntry: MealPlanEntry | null;
    onSaved: () => void;
};

const RecipePickerDialog = (props: RecipePickerDialogProps) => {
    const {t} = useTranslation();
    const [recipes, setRecipes] = useState<Recipe[]>([]);
    const [filter, setFilter] = useState<string>('');
    const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
    const [servings, setServings] = useState<number>(1);

    useEffect(() => {
        if(props.open) {
            getAllRecipes().then((allRecipes: Recipe[]) => setRecipes(allRecipes));
            setSelectedRecipe(null);
            setFilter('');
        }
    }, [props.open]);

    const filteredRecipes = useMemo(() => {
        return recipes.filter(recipe => recipe.name.toLowerCase().includes(filter.toLowerCase()));
    }, [recipes, filter]);

    const selectRecipe = useCallback((recipe: Recipe) => {
        setSelectedRecipe(recipe);
        setServings(props.existingEntry?.servings ?? recipe.yield);
    }, [props.existingEntry]);

    const confirm = useCallback(() => {
        if(!selectedRecipe || selectedRecipe.id === null) {
            return;
        }

        const entry: MealPlanEntry = {
            id: props.existingEntry?.id ?? null,
            date: props.date,
            mealType: props.mealType,
            servings: servings,
            recipeId: selectedRecipe.id
        };

        saveMealPlanEntry(entry).then(() => {
            props.onSaved();
        });
    }, [selectedRecipe, servings, props]);

    return (
        <Dialog open={props.open} onClose={() => props.setOpen(false)} fullWidth maxWidth="xs">
            <DialogTitle>{t('selectRecipe')}</DialogTitle>
            <DialogContent>
                {!selectedRecipe ? (
                    <>
                        <TextField fullWidth size="small" placeholder={t('name')} value={filter} onChange={(e) => setFilter(e.target.value)} sx={{marginBottom: '10px'}} />
                        <List style={{maxHeight: '320px', overflowY: 'auto'}}>
                            {filteredRecipes.map(recipe => (
                                <ListItem disablePadding key={recipe.id}>
                                    <ListItemButton onClick={() => selectRecipe(recipe)}>
                                        <ListItemText primary={recipe.name} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </>
                ) : (
                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px'}}>
                        <div><b>{selectedRecipe.name}</b></div>
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <IconButton aria-label="minus" onClick={() => setServings((s) => Math.max(1, s - 1))}><RemoveIcon /></IconButton>
                            <div style={{margin: '0 8px'}}>{servings} {t('portions')}</div>
                            <IconButton aria-label="plus" onClick={() => setServings((s) => s + 1)}><AddIcon /></IconButton>
                        </div>
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                {selectedRecipe && <Button onClick={confirm} variant="contained">{t('confirm')}</Button>}
                <Button onClick={() => props.setOpen(false)}>{t('cancel')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default RecipePickerDialog;
